/**
 * Dynamic sprite atlas: glyphs, counters, markers and particles are drawn once with Canvas2D
 * into one big mipmapped texture (shelf packing, lazily keyed), so every sprite layer on the map
 * samples the same texture and batches into single instanced draws.
 *
 * Two zones share the texture: permanent frames pack downwards from the top, **volatile** frames
 * (unit counters — their set depends on the counter style, the language and which factions and
 * variants are on the map) pack upwards from the bottom. `resetVolatile()` drops the volatile
 * zone wholesale, so a style switch redraws only what is shown and the atlas never fills up with
 * counters nobody looks at any more.
 */
import { CanvasSource, Texture } from 'pixi.js';

export interface Frame {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  /** Size in atlas pixels. */
  w: number;
  h: number;
}

export type DrawFn = (ctx: CanvasRenderingContext2D, w: number, h: number) => void;

const PAD = 4;

export class Atlas {
  readonly texture: Texture;
  readonly size: number;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly frames = new Map<string, Frame>();
  private x = PAD;
  private y = PAD;
  private rowH = 0;
  /** Volatile zone cursor: `vx` along the current row, `vy` the row's bottom edge. */
  private vx = PAD;
  private vy: number;
  private vRowH = 0;
  /** Top edge of the volatile zone (the permanent zone must stay above it). */
  private vTop: number;
  private readonly volatileKeys = new Set<string>();
  private dirty = false;
  /** Bumped by every `resetVolatile()`: frames obtained before are stale. */
  volatileVersion = 0;
  /** Solid white square: tinted rectangles (bars, fills). */
  readonly white: Frame;

  constructor(size = 2048) {
    this.size = size;
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.canvas.height = size;
    this.vy = this.vTop = size - PAD;
    const ctx = this.canvas.getContext('2d', { willReadFrequently: false });
    if (!ctx) throw new Error('2d canvas unavailable');
    this.ctx = ctx;
    const source = new CanvasSource({
      resource: this.canvas,
      autoGenerateMipmaps: true,
      scaleMode: 'linear',
      mipmapFilter: 'linear',
      addressMode: 'clamp-to-edge',
    });
    this.texture = new Texture({ source });
    // A white square whose centre texels are safe from mip bleeding.
    const white = this.get('__white', 16, 16, (c, w, h) => {
      c.fillStyle = '#fff';
      c.fillRect(-PAD, -PAD, w + PAD * 2, h + PAD * 2);
    });
    const du = (white.u1 - white.u0) * 0.3;
    const dv = (white.v1 - white.v0) * 0.3;
    this.white = { u0: white.u0 + du, v0: white.v0 + dv, u1: white.u1 - du, v1: white.v1 - dv, w: 1, h: 1 };
  }

  has(key: string): boolean {
    return this.frames.has(key);
  }

  /**
   * Frame for `key`, drawing it on first use. `draw` gets a context translated to the frame.
   * `volatile` frames go to the bottom zone; when it is full it is reset (callers holding volatile
   * frames watch `volatileVersion`).
   */
  get(key: string, w: number, h: number, draw: DrawFn, volatile = false): Frame {
    const hit = this.frames.get(key);
    if (hit) return hit;
    w = Math.ceil(w);
    h = Math.ceil(h);
    const at = volatile ? this.placeVolatile(w, h) : this.placePermanent(w, h);
    if (!at) {
      // Atlas full: reuse the white frame rather than crash (should never happen in practice).
      console.warn(`[render] atlas full, cannot place ${key}`);
      return this.white;
    }
    const [x, y] = at;
    if (volatile) this.volatileKeys.add(key);
    const c = this.ctx;
    c.save();
    c.beginPath();
    c.rect(x - PAD, y - PAD, w + PAD * 2, h + PAD * 2);
    c.clip();
    c.translate(x, y);
    draw(c, w, h);
    c.restore();
    const s = this.size;
    const frame: Frame = { u0: x / s, v0: y / s, u1: (x + w) / s, v1: (y + h) / s, w, h };
    this.frames.set(key, frame);
    this.dirty = true;
    return frame;
  }

  private placePermanent(w: number, h: number): [number, number] | null {
    if (this.x + w + PAD > this.size) {
      this.x = PAD;
      this.y += this.rowH + PAD * 2;
      this.rowH = 0;
    }
    if (this.y + h + PAD > this.vTop - PAD) return null;
    const at: [number, number] = [this.x, this.y];
    this.x += w + PAD * 2;
    this.rowH = Math.max(this.rowH, h);
    return at;
  }

  private placeVolatile(w: number, h: number, retry = true): [number, number] | null {
    if (this.vx + w + PAD > this.size) {
      this.vx = PAD;
      this.vy -= this.vRowH + PAD * 2;
      this.vRowH = 0;
    }
    const top = this.vy - Math.max(this.vRowH, h);
    if (top - PAD < this.y + this.rowH + PAD) {
      if (!retry) return null;
      this.resetVolatile();
      return this.placeVolatile(w, h, false);
    }
    const at: [number, number] = [this.vx, this.vy - h];
    this.vx += w + PAD * 2;
    this.vRowH = Math.max(this.vRowH, h);
    this.vTop = Math.min(this.vTop, this.vy - this.vRowH);
    return at;
  }

  /** Drop every volatile frame (their pixels and keys); callers re-request what they need. */
  resetVolatile(): void {
    if (this.volatileKeys.size === 0) return;
    for (const k of this.volatileKeys) this.frames.delete(k);
    this.volatileKeys.clear();
    this.ctx.clearRect(0, this.vTop - PAD, this.size, this.size - this.vTop + PAD);
    this.vx = PAD;
    this.vy = this.vTop = this.size - PAD;
    this.vRowH = 0;
    this.volatileVersion++;
    this.dirty = true;
  }

  /** Re-upload the canvas if frames were added (call once per frame before rendering). */
  flush(): void {
    if (!this.dirty) return;
    this.dirty = false;
    this.texture.source.update();
  }

  destroy(): void {
    this.texture.destroy(true);
    this.frames.clear();
  }
}
